import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import CircularProgress from "@material-ui/core/CircularProgress";
import { isEmpty } from "lodash";
import React, { useEffect } from "react";
import { Calendar } from "../chat-plus-calendar/Calendar";
import { NoRouteView } from "../no-route-view";
import usePlannerMetaData from "../web-app-calendar/hooks/usePlannerMetaData";
import { StorageKeyPrefixProvider } from "../web-app-calendar/hooks/useStorageKeyPrefix";
import Context from "./Context";
import SharedProposal from "./SharedProposal";
import { SharedProposalCalendarDateSync } from "./SharedProposalCalendarDateSync";
import { useSetProposalError } from "./hooks/ProposalErrorContext";
import { useSharedProposalContext } from "./hooks/SharedProposalProvider";

export const ProposalLayout = () => {
  const [notFound, setNotFound] = React.useState(false);
  const { primaryCalendarId, loading: plannerMetaDataLoading } = usePlannerMetaData();
  const setError = useSetProposalError();
  const {
    ownerCalendarId,
    hasEditPermission,
    viewerIsProposalOwner,
    proposal,
    proposalLoading,
    errorMessage,
    setUpdatedProposal,
  } = useSharedProposalContext();

  useEffect(() => {
    if (!isEmpty(errorMessage)) {
      if (errorMessage?.includes("404")) {
        setNotFound(true);
      } else {
        setError({
          error: new Error(errorMessage ?? "Error fetching proposal"),
          message:
            "Oops! Something went wrong while fetching the proposal. Please try again later.",
          proposalId: proposal?.proposalId || null,
        });
      }
    }
  }, [errorMessage]);

  if (notFound || (!hasEditPermission && !viewerIsProposalOwner)) {
    return <NoRouteView />;
  }

  if (proposalLoading || plannerMetaDataLoading || !primaryCalendarId || !proposal) {
    return <CircularProgress />;
  }

  const diff = proposal.diffBlocks[0]?.diffs[0];
  const attendees = getValue(diff?.attendees?.attendeePeopleErrorable)?.list ?? [];
  const attendeesPrimaryCalendarIds = attendees.map((attendee) => attendee.primaryCalendar);
  const userCanConfirmProposal = attendeesPrimaryCalendarIds.includes(primaryCalendarId);

  if (!userCanConfirmProposal) {
    setError({
      error: new Error(
        `User does not have access to this proposal. ${primaryCalendarId}. ${attendeesPrimaryCalendarIds.join(
          ",",
        )}`,
      ),
      message: "User does not have access to this proposal",
      proposalId: proposal.proposalId,
    });

    return <NoRouteView />;
  }

  return (
    <Context>
      <SharedProposalCalendarDateSync />
      <div
        cw-id="proposalLayout"
        className="cw-flex cw-flex-row cw-grow cw-justify-items-end cw-w-full cw-h-full"
      >
        <div className="cw-self-end cw-h-full cw-flex lg:cw-w-[420px] cw-w-full  cw-border-r cw-border-subtle cw-border-solid">
          <SharedProposal
            setUpdatedProposal={setUpdatedProposal}
            sharedProposal={proposal}
            ownerCalendarId={ownerCalendarId}
          />
        </div>
        <div className="cw-grow cw-flex-1 cw-overflow-hidden">
          <div className="lg:cw-flex cw-hidden lg:cw-w-full cw-transition-all cw-h-full">
            <StorageKeyPrefixProvider prefix="proposal">
              <Calendar />
            </StorageKeyPrefixProvider>
          </div>
        </div>
      </div>
    </Context>
  );
};
